import { type Coach } from '@entities/coaches/index';
import { memo, useCallback } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { useNavigate } from 'react-router-dom';
import { usePersonalRedirect } from '@pages/personal/model.ts';
import { CoachCardSmall } from '@features/coaches/coachCard/Small/ui';
import { SSMagicBox } from '@features/coaches/infinite-list/styles';
import { useUser } from '@shared/hooks/index.ts';
import { useCoachesInfiniteQuery } from '@src/api/api-hooks/coaches/useCoachesInfiniteQuery.ts';
import { Blank, Loader } from '@src/library/components/common';

type PT = {
  isFilterOpen: boolean;
  consultationType?: string;
  selectedCity?: string;
  tags?: string;
  partitions: number[];
  searchByName?: string;
};

export const InfiniteNewList = memo(
  ({ isFilterOpen, searchByName, tags, consultationType, selectedCity, partitions }: PT) => {
    const navigate = useNavigate();
    const { redirectOnPersonalPageWithSaveUrl } = usePersonalRedirect();
    const { user: currentSessionUser } = useUser();

    const {
      data: coachesData,
      isFetchingNextPage,
      fetchNextPage,
      hasNextPage,
      isLoading: isLoadingCoaches,
      isError,
    } = useCoachesInfiniteQuery({
      consultations_type: consultationType || undefined,
      search: searchByName || undefined,
      city: selectedCity || undefined,
      tags: tags || undefined,
      partitions: partitions.length ? partitions.join(' ,') : undefined,
    });

    const [sentryRef] = useInfiniteScroll({
      loading: isFetchingNextPage,
      hasNextPage: hasNextPage,
      onLoadMore: fetchNextPage,
      rootMargin: '0px 0px 100px 0px',
    });

    const onCoachCardClick = useCallback(
      (coach: Coach) =>
        coach?.user_id === currentSessionUser?.id
          ? redirectOnPersonalPageWithSaveUrl()
          : navigate(String(coach.id)),
      [currentSessionUser, navigate, redirectOnPersonalPageWithSaveUrl]
    );

    if (isError) {
      return 'Error';
    }

    return (
      <SSMagicBox $shouldHideOnMobileL={isFilterOpen}>
        {isLoadingCoaches && <Loader />}
        {coachesData?.results.length === 0 && <Blank text="В списке нет ни одного специалиста" />}

        {coachesData?.results.map((coach) => (
          <CoachCardSmall onClick={() => onCoachCardClick(coach)} key={coach.id} {...coach} />
        ))}

        {isFetchingNextPage ? <Loader /> : <div ref={sentryRef}></div>}
      </SSMagicBox>
    );
  }
);
